// The SDK owns the in-memory access token, silent refresh and the error shape.
// Never use apiRequest for downloads (it always parses JSON): fetch to a blob instead.
import { apiRequest, ApiClientError, getAccessToken } from '@skaleminds/jig-auth-sdk-ui';

export { apiRequest, ApiClientError, setAccessToken, clearAccessToken } from '@skaleminds/jig-auth-sdk-ui';

const HTTP_UNAUTHORISED = 401;
const UNKNOWN_ERROR_MESSAGE = 'An unexpected error occurred';
const FILENAME_PATTERN = /filename="([^"]+)"/;

async function sendWithToken(path: string, init: RequestInit): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return fetch(path, { ...init, headers, credentials: 'include' });
}

/**
 * Sends with the SDK's token; on a 401 delegates one authenticated JSON call to apiRequest, which
 * runs the SDK's silent refresh (or its /login redirect), then retries once with the new token.
 */
async function sendWithAuthRetry(path: string, init: RequestInit): Promise<Response> {
  const response = await sendWithToken(path, init);
  if (response.status !== HTTP_UNAUTHORISED) return response;
  await apiRequest<unknown>('/api/me');
  return sendWithToken(path, init);
}

async function errorFrom(response: Response): Promise<ApiClientError> {
  let body: { error?: { code?: string; message?: string } } = {};
  try {
    body = (await response.json()) as typeof body;
  } catch {
    // A proxy error page (HTML) or an empty body: fall through to the generic error.
  }
  return new ApiClientError(body.error?.message ?? UNKNOWN_ERROR_MESSAGE, body.error?.code ?? 'UNKNOWN_ERROR', response.status);
}

/**
 * For endpoints that answer 204 No Content (DELETE /api/entries/:id, DELETE /api/timer). The SDK's
 * apiRequest always calls response.json(), which throws on an empty body.
 */
export async function apiRequestNoContent(path: string, init: RequestInit = {}): Promise<void> {
  const response = await sendWithAuthRetry(path, init);
  if (response.ok) return;
  throw await errorFrom(response);
}

/**
 * GET a file with the SDK token (Bearer) + cookies, one silent-refresh retry on 401, then save it via
 * a temporary <a download>. Never window.open: the cookie may be absent until a refresh.
 */
export async function downloadFile(path: string, fallbackFilename: string): Promise<void> {
  await saveResponse(await sendWithAuthRetry(path, { method: 'GET' }), fallbackFilename);
}

const JSON_HEADERS = { 'Content-Type': 'application/json' } as const;

/**
 * Sprint 18: a share-link download for a signed-in viewer (POST /api/shared/report/*). The token travels in
 * the JSON body, never the URL; same Bearer + one silent-refresh retry as downloadFile.
 */
export async function downloadPostFile(path: string, body: unknown, fallbackFilename: string): Promise<void> {
  await saveResponse(await sendWithAuthRetry(path, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) }), fallbackFilename);
}

/**
 * Sprint 18: a public-link download for an anonymous viewer (POST /api/public/share/report/*). No token, no
 * cookies (credentials omitted), no refresh: the public routes never read either.
 */
export async function downloadPublicFile(path: string, body: unknown, fallbackFilename: string): Promise<void> {
  await saveResponse(await fetch(path, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body), credentials: 'omit' }), fallbackFilename);
}

async function saveResponse(response: Response, fallbackFilename: string): Promise<void> {
  if (!response.ok) throw await errorFrom(response);
  const blob = await response.blob();
  const match = FILENAME_PATTERN.exec(response.headers.get('Content-Disposition') ?? '');
  const filename = match?.[1] ?? fallbackFilename;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
